import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { RESOURCE_ROUTES, RESOURCE_LABELS, itemTitle } from "@/lib/searchRoutes";

const SearchBox = ({ placeholder, className = "", inputTestId }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounce.current = setTimeout(() => {
      api.get(`/public/search/all?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setResults(r.data.results.slice(0, 6)))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (to) => {
    setOpen(false);
    setQ("");
    setResults([]);
    navigate(to);
  };

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) go(`/buscar?q=${encodeURIComponent(q.trim())}`);
  };

  const showDropdown = open && q.trim().length >= 2;

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <form onSubmit={submit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            data-testid={inputTestId}
            placeholder={placeholder}
            className="w-full pl-9 pr-9 py-2.5 md:py-2 rounded-full bg-muted border border-transparent focus:border-primary focus:bg-card outline-none text-sm transition-colors"
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2" aria-label="Limpar">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </form>

      {showDropdown && (
        <div className="absolute left-0 right-0 mt-2 bg-card rounded-2xl border border-border shadow-lg overflow-hidden z-50" data-testid="search-suggestions">
          {loading && results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">Buscando...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">Nenhum resultado para "{q}"</div>
          ) : (
            <>
              {results.map(({ resource, item }, idx) => {
                const img = item.cover || item.image || item.logo || (item.photos && item.photos[0]);
                return (
                  <button
                    key={`${resource}-${item.id}-${idx}`}
                    type="button"
                    onClick={() => go(RESOURCE_ROUTES[resource]?.(item) || "#")}
                    data-testid={`suggestion-${idx}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted text-left transition-colors"
                  >
                    {img ? (
                      <img src={fileUrl(img)} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-muted shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">{RESOURCE_LABELS[resource]}</div>
                      <div className="text-sm font-medium text-foreground truncate">{itemTitle(item)}</div>
                    </div>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => go(`/buscar?q=${encodeURIComponent(q.trim())}`)}
                data-testid="suggestion-see-all"
                className="w-full flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-semibold text-primary border-t border-border hover:bg-muted transition-colors"
              >
                Ver todos os resultados <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
